import { Injectable, signal, untracked } from '@angular/core';
import { saveAs } from 'file-saver';

import { environment } from '../../../environments/environment';
import {
  DiagnosticEntry,
  DiagnosticFinishReason,
  DiagnosticOmittedEntry,
  DiagnosticReport,
  DiagnosticReportEntry,
  FrontendDiagnosticOutcomeOptions,
} from './diagnostic.interfaces';

const MAX_DURATION_MS = 15 * 60 * 1000;
const MAX_REQUESTS = 60;
const MAX_TOTAL_BYTES = 5 * 1024 * 1024;
const REPORT_OVERHEAD_BYTES = 16 * 1024;
const MAX_ENTRIES_BYTES = MAX_TOTAL_BYTES - REPORT_OVERHEAD_BYTES;

@Injectable({ providedIn: 'root' })
export class DiagnosticSessionService {
  readonly isActive = signal(false);
  readonly diagnosticSessionId = signal<string | null>(null);
  readonly requestCount = signal(0);
  readonly totalBytes = signal(0);
  readonly lastSupportId = signal<string | null>(null);

  private entries: DiagnosticReportEntry[] = [];
  private startedAt: Date | null = null;
  private expirationTimer: ReturnType<typeof setTimeout> | null = null;
  private initialized = false;
  private readonly encoder = new TextEncoder();

  initialize(): void {
    if (this.initialized || typeof window === 'undefined') return;

    window.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('pagehide', this.handlePageHide);
    this.initialized = true;
  }

  getSessionId(): string | null {
    // Los interceptores necesitan una fotografia del estado, no una dependencia
    // reactiva del effect que haya originado la solicitud HTTP.
    return untracked(() => (this.isActive() ? this.diagnosticSessionId() : null));
  }

  createRequestId(): string {
    return this.createIdentifier();
  }

  capture(entry: DiagnosticEntry): void {
    const sessionId = this.getSessionId();
    if (!sessionId || entry.diagnosticSessionId !== sessionId) return;

    const entryBytes = this.measureBytes(entry);
    const nextRequestCount = this.requestCount() + 1;

    if (this.totalBytes() + entryBytes > MAX_ENTRIES_BYTES) {
      const omittedEntry: DiagnosticOmittedEntry = {
        diagnosticSessionId: entry.diagnosticSessionId,
        requestId: entry.requestId,
        timestamp: entry.timestamp,
        method: entry.method,
        route: entry.route,
        operation: entry.operation,
        durationMs: entry.durationMs,
        truncated: true,
        omissionReason: 'MAX_TOTAL_BYTES',
      };

      this.entries.push(omittedEntry);
      this.requestCount.set(nextRequestCount);
      this.finishAndDownload('MAX_TOTAL_BYTES');
      return;
    }

    this.entries.push(entry);
    this.requestCount.set(nextRequestCount);
    this.totalBytes.update(total => total + entryBytes);

    if (nextRequestCount >= MAX_REQUESTS) {
      this.finishAndDownload('MAX_REQUESTS');
    }
  }

  captureFrontendOutcome(options: FrontendDiagnosticOutcomeOptions): void {
    const sessionId = this.getSessionId();
    if (!sessionId) return;

    const timestamp = new Date().toISOString();
    this.capture({
      diagnosticSessionId: sessionId,
      requestId: options.requestId,
      timestamp,
      method: options.method,
      route: 'unresolved-client',
      durationMs: Math.max(0, Date.now() - options.startedAt),
      events: [
        {
          type: options.outcome,
          timestamp,
          sequence: 1,
          source: 'frontend',
          metadata: {
            statusCode: options.statusCode,
            responseType: options.responseType,
            outcome: options.outcome,
            contentType: options.contentType ?? undefined,
          },
        },
      ],
    });
  }

  registerErrorSupportId(supportId: unknown, fallbackRequestId: string): void {
    const normalizedSupportId = typeof supportId === 'string' && supportId.length > 0 ? supportId : fallbackRequestId;
    this.lastSupportId.set(normalizedSupportId);
  }

  toggleFromControlPanel(): void {
    if (this.isActive()) {
      this.finishAndDownload('MANUAL');
      return;
    }

    this.start();
  }

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (!this.isActive() || !event.ctrlKey || !event.altKey || event.key.toLowerCase() !== 'd' || event.repeat) return;

    event.preventDefault();
    event.stopPropagation();
    this.finishAndDownload('MANUAL');
  };

  private readonly handlePageHide = (): void => {
    this.clear();
  };

  private start(): void {
    this.clear();

    const sessionId = this.createIdentifier();
    this.entries = [];
    this.startedAt = new Date();
    this.diagnosticSessionId.set(sessionId);
    this.requestCount.set(0);
    this.totalBytes.set(0);
    this.lastSupportId.set(null);
    this.isActive.set(true);
    this.expirationTimer = setTimeout(() => this.finishAndDownload('MAX_DURATION'), MAX_DURATION_MS);
  }

  private finishAndDownload(reason: DiagnosticFinishReason): void {
    const sessionId = this.diagnosticSessionId();
    const sessionStartedAt = this.startedAt;

    if (!this.isActive() || !sessionId || !sessionStartedAt) return;

    const finishedAt = new Date();
    const report: DiagnosticReport = {
      format: 'ecenter-diagnostic-v1',
      diagnosticSessionId: sessionId,
      environment: environment.env,
      startedAt: sessionStartedAt.toISOString(),
      finishedAt: finishedAt.toISOString(),
      finishReason: reason,
      requestCount: this.requestCount(),
      capturedEntryBytes: this.totalBytes(),
      totalBytes: 0,
      limits: {
        durationMinutes: MAX_DURATION_MS / 60000,
        requests: MAX_REQUESTS,
        totalBytes: MAX_TOTAL_BYTES,
      },
      entries: [...this.entries],
    };

    const formattedContents = this.serializeReport(report, true);
    const contents =
      this.encoder.encode(formattedContents).byteLength <= MAX_TOTAL_BYTES
        ? formattedContents
        : this.serializeReport(report, false);
    const filename = this.createFilename(finishedAt, sessionId);

    try {
      saveAs(new Blob([contents], { type: 'text/plain;charset=utf-8' }), filename);
    } finally {
      this.clear();
    }
  }

  private clear(): void {
    if (this.expirationTimer) clearTimeout(this.expirationTimer);

    this.expirationTimer = null;
    this.entries = [];
    this.startedAt = null;
    this.isActive.set(false);
    this.diagnosticSessionId.set(null);
    this.requestCount.set(0);
    this.totalBytes.set(0);
    this.lastSupportId.set(null);
  }

  private createIdentifier(): string {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();

    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hexadecimal = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');

    return `${hexadecimal.slice(0, 8)}-${hexadecimal.slice(8, 12)}-${hexadecimal.slice(12, 16)}-${hexadecimal.slice(16, 20)}-${hexadecimal.slice(20)}`;
  }

  private createFilename(finishedAt: Date, sessionId: string): string {
    const timestamp = finishedAt
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}Z$/, 'Z');
    return `ecenter-diagnostico-${timestamp}-${sessionId.slice(0, 8)}.txt`;
  }

  private measureBytes(value: unknown): number {
    return this.encoder.encode(JSON.stringify(value)).byteLength;
  }

  private serializeReport(report: DiagnosticReport, formatted: boolean): string {
    const indentation = formatted ? 2 : undefined;
    let contents = JSON.stringify(report, null, indentation);

    for (let attempt = 0; attempt < 3; attempt += 1) {
      const actualBytes = this.encoder.encode(contents).byteLength;
      if (report.totalBytes === actualBytes) return contents;

      report.totalBytes = actualBytes;
      contents = JSON.stringify(report, null, indentation);
    }

    return contents;
  }
}
